import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import List from '@mui/material/List';
import type DocumentTreeNode from '../../Models/DocumentTreeNodeModel';
import type DocumentNode from '../../Models/DocumentNodeModel';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import { IconButton, Link, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faFile, faFolder } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import DocumentInfoDialog from '../../Dialogs/DocumentDialogs/DocumentInfoDialog';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

type DocumentTreeNodeViewComponentProps = {
    treeNodeId: number,
};

function DocumentTreeNodeViewComponent(props: Readonly<DocumentTreeNodeViewComponentProps>): JSX.Element {

    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);

    const navigate: NavigateFunction = useNavigate();

    const currentTheme: Theme = useTheme();

    const [treeNodes, setTreeNodes] = useState<DocumentTreeNode[]>([]);
    const [documentNodes, setDocumentNodes] = useState<DocumentNode[]>([]);
    const [showDocumentInfoDialog, setShowDocumentInfoDialog] = useState<string | null>(null);

    const path: number[] = useMemo<number[]>(() => {

        const newPath: number[] = [];

        let currentTreeNode: DocumentTreeNode | null = treeNodes.find((treeNode: DocumentTreeNode) => treeNode.nodeId === props.treeNodeId) ?? null;

        while (currentTreeNode !== null) {

            if (currentTreeNode.nodeId !== props.treeNodeId) {
                newPath.push(currentTreeNode.nodeId);
            }

            // eslint-disable-next-line @typescript-eslint/no-loop-func
            currentTreeNode = treeNodes.find((treeNode: DocumentTreeNode) => treeNode.nodeId === currentTreeNode?.parentId) ?? null;
        }

        const newPathReversed: number[] = [...newPath].reverse();

        return newPathReversed;
    }, [props.treeNodeId, treeNodes]);

    const breadCrumbPath: JSX.Element[] = useMemo<JSX.Element[]>(() => path.map((nodeId: number, index: number) => {

        const treeNodeName: string = treeNodes.find((currentNode: DocumentTreeNode) => currentNode.nodeId === nodeId)?.name ?? 'ERROR';
        
        return (
            <Link
                key={nodeId.toString() + '_' + index.toString()}
                underline='hover'
                onClick={(): void => {
                    updateLastActivity(userContext, localStorageContext);
                    navigate('/documents/' + nodeId.toString());
                }}
            >
                {treeNodeName}
            </Link>
        );
    }), [localStorageContext, navigate, path, treeNodes, userContext]);

    const listItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const childTreeNodes: DocumentTreeNode[] = treeNodes.filter((node: DocumentTreeNode) => node.parentId === props.treeNodeId);
        const childDocumentNodes: DocumentNode[] = documentNodes.filter((documentNode: DocumentNode) => documentNode.treeNodeId === props.treeNodeId);

        const sortedChildTreeNodes: DocumentTreeNode[] = [...childTreeNodes].sort((a: DocumentTreeNode, b: DocumentTreeNode) => a.sortNumber - b.sortNumber);
        const sortedChildDocumentNodes: DocumentNode[] = [...childDocumentNodes].sort((a: DocumentNode, b: DocumentNode) => a.name.localeCompare(b.name));

        return sortedChildTreeNodes.map((treeNode: DocumentTreeNode) => {
            return (
                <ListItem
                    key={treeNode.nodeId}
                    disablePadding
                    style={{
                        marginBottom: '10px',
                    }}
                >
                    <ListItemButton onClick={(): void => {
                        updateLastActivity(userContext, localStorageContext);
                        navigate('/documents/' + treeNode.nodeId.toString());
                    }}>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faFolder} />
                        </ListItemIcon>
                        <ListItemText
                            primary={treeNode.name}
                            style={{
                                color: currentTheme.palette.text.primary,  
                            }}    
                        />
                    </ListItemButton>
                </ListItem>
            );
        }).concat(sortedChildDocumentNodes.map((documentNode: DocumentNode) => {
            return (
                <ListItem
                    key={documentNode.documentId}
                    disablePadding
                    style={{
                        marginBottom: '10px',
                    }}
                >
                    <ListItemButton onClick={(): void => setShowDocumentInfoDialog(documentNode.documentId)}>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faFile} />
                        </ListItemIcon>
                        <ListItemText
                            primary={documentNode.name}
                            style={{
                                color: currentTheme.palette.text.primary,  
                            }}    
                        />
                    </ListItemButton>
                </ListItem>
            );
        }));
    }, [currentTheme.palette.text.primary, documentNodes, localStorageContext, navigate, props.treeNodeId, treeNodes, userContext]);

    useEffect(() => {

        async function fetchData(): Promise<void> {

            const newTreeNodes: DocumentTreeNode[] = await indexedDBContext.indexedDBService.getAllDocumentTreeNodes();
            const newDocumentNodes: DocumentNode[] = await indexedDBContext.indexedDBService.getAllDocumentNodes();

            setTreeNodes(newTreeNodes);
            setDocumentNodes(newDocumentNodes);
        }

        void fetchData();
    }, [indexedDBContext.indexedDBService]);

    return (
        <>
            <div>
                {treeNodes.length === 0
                    ? (
                        <p style={{
                            color: currentTheme.palette.text.primary,
                            margin: '15px 0px 0px 15px',
                        }}>
                            No documents found
                        </p>
                    )
                    : (
                        <>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>
                                            <IconButton 
                                                edge='end' 
                                                color='inherit' 
                                                disabled={path.length === 0}
                                                onClick={(): void => {
                                                    updateLastActivity(userContext, localStorageContext);
                                                    if (path.length > 0) {
                                                        navigate('/documents/' + (path[path.length - 1]?.toString() ?? ''));
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowUp}
                                                    color={path.length === 0 ? currentTheme.palette.action.disabled : currentTheme.palette.text.primary}    
                                                />
                                            </IconButton>
                                        </td>
                                        <td>
                                            <Breadcrumbs style={{
                                                marginLeft: '15px',
                                            }}>
                                                {breadCrumbPath}
                                            </Breadcrumbs>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            {listItems.length === 0
                                ? (
                                    <p style={{
                                        color: currentTheme.palette.text.primary,
                                        margin: '15px 0px 0px 15px',
                                    }}>
                                        Nothing to show here
                                    </p>
                                )
                                : (
                                    <List>
                                        {listItems}
                                    </List>
                                )
                            }
                        </>
                    )
                }
            </div>

            <DocumentInfoDialog
                controller={showDocumentInfoDialog !== null}
                onClose={(): void => setShowDocumentInfoDialog(null)}
                documentNode={documentNodes.find((documentNode: DocumentNode) => documentNode.documentId === showDocumentInfoDialog) ?? null}
            />
        </>
    );
}

export default DocumentTreeNodeViewComponent;